import { z } from 'zod';
import { FlowTestAssertionSchema } from '../flowTest/assertion.js';
import { FlowTestStartPayloadSchema } from '../flowTest/startPayload.js';
import { FlowTestBlockMocksByIdSchema, FlowTestExpectedRunStateSchema, } from '../flowTest/testCase.js';
export const FlowTestGenerationRequestStatusSchema = z.enum([
    'Pending',
    'Processing',
    'Succeeded',
    'Failed',
]);
export const FlowTestGenerationRequestStatus = FlowTestGenerationRequestStatusSchema.enum;
export const FlowTestCaseBranchKindSchema = z.enum(['Conditional', 'Switch', 'Error']);
export const FlowTestCaseBranchKind = FlowTestCaseBranchKindSchema.enum;
/** Reports stored before Predict authored the runnable fields lack them. */
export const GeneratedFlowTestCaseSchema = z.object({
    name: z.string(),
    description: z.string(),
    expectedRunState: FlowTestExpectedRunStateSchema.optional(),
    startPayload: FlowTestStartPayloadSchema.optional(),
    blockOutputMocks: FlowTestBlockMocksByIdSchema.optional(),
    assertions: z.array(FlowTestAssertionSchema).optional(),
});
export const FlowTestCaseBranchApiSchema = z.object({
    key: z.string(),
    block_id: z.string(),
    block_name: z.string(),
    kind: FlowTestCaseBranchKindSchema,
    outcome: z.string(),
    description: z.string(),
});
const GeneratedFlowTestCaseApiSchema = z.object({
    name: z.string(),
    description: z.string(),
    expected_run_state: FlowTestExpectedRunStateSchema,
    start_payload: FlowTestStartPayloadSchema.nullable(),
    block_output_mocks: FlowTestBlockMocksByIdSchema,
    assertions: z.array(FlowTestAssertionSchema),
});
export const FlowTestGenerationApiReportSchema = z.object({
    test_cases: z.array(GeneratedFlowTestCaseApiSchema),
    coverage: z.object({
        total_branches: z.number(),
        covered_branches: z.number(),
        covered_branch_keys: z.array(z.string()),
        uncovered_branches: z.array(FlowTestCaseBranchApiSchema),
    }),
    // Optional because Predict and flow-api deploy independently.
    usage: z
        .object({
        model_name: z.string(),
        input_tokens: z.number().int(),
        cached_input_tokens: z.number().int(),
        output_tokens: z.number().int(),
    })
        .optional(),
});
export const FlowTestCaseBranchSchema = z.object({
    key: z.string(),
    blockId: z.string(),
    blockName: z.string(),
    kind: FlowTestCaseBranchKindSchema,
    outcome: z.string(),
    description: z.string(),
});
export const FlowTestGenerationCoverageSchema = z.object({
    totalBranches: z.number(),
    coveredBranches: z.number(),
    coveredBranchKeys: z.array(z.string()),
    uncoveredBranches: z.array(FlowTestCaseBranchSchema),
});
export const SkippedGeneratedFlowTestCaseSchema = z.object({
    name: z.string(),
    reason: z.string(),
});
export const FlowTestSuiteSaveOutcomeSchema = z.object({
    saved: z.number().int().nonnegative(),
    skipped: z.array(SkippedGeneratedFlowTestCaseSchema),
});
export const FlowTestGenerationUsageSchema = z.object({
    modelName: z.string(),
    inputTokens: z.number().int(),
    cachedInputTokens: z.number().int(),
    outputTokens: z.number().int(),
});
export const FlowTestGenerationResultSchema = z.object({
    testCases: z.array(GeneratedFlowTestCaseSchema),
    coverage: FlowTestGenerationCoverageSchema,
    /** Identifies the flow and sub-flow definitions the cases were generated against. */
    definitionsHash: z.string().optional(),
    savedToSuite: FlowTestSuiteSaveOutcomeSchema.optional(),
    usage: FlowTestGenerationUsageSchema.optional(),
});
export const BaseFlowTestGenerationRequestSchema = z.object({
    flowDefinitionId: z.uuid(),
    organizationId: z.uuid(),
    requestedById: z.uuid(),
});
export const FlowTestGenerationRequestSchema = BaseFlowTestGenerationRequestSchema.extend({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    status: FlowTestGenerationRequestStatusSchema,
    result: FlowTestGenerationResultSchema.nullable(),
    errorMessage: z.string().nullable(),
    resetAt: z.iso.datetime().nullable(),
    resetById: z.uuid().nullable(),
    modelName: z.string().nullable(),
    inputTokens: z.number().int().nullable(),
    cachedInputTokens: z.number().int().nullable(),
    outputTokens: z.number().int().nullable(),
});
/**
 * Body `POST /test-case-generation/generate` accepts. The organization and the user come
 * from the caller's session rather than the body, which is why only the definition is here.
 */
export const FlowTestGenerationStartBodySchema = z.object({
    flowDefinitionId: z.uuid().describe('The definition to write test cases for.'),
});
/** Path parameters the endpoints addressing one generation request accept. */
export const FlowTestGenerationRequestIdParamsSchema = z.object({
    requestId: z.uuid().describe('The generation request.'),
});
//# sourceMappingURL=flowTestGeneration.js.map